import * as React from 'react';
import {
    List,
    RaRecord, useNotify, Confirm,
} from 'react-admin';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Divider,
    Button,
    Stack,
    Chip,
    Card,
    CircularProgress, Select, MenuItem,
} from '@mui/material';
import { Switch } from '../../components/switch/Switch.tsx';
import { useTranslation } from 'react-i18next';
import { useOrdersList } from "../../hooks/useOrderStatus.ts";
import { useOrdersListVendor } from "../../hooks/useOrderStatusVendor.ts";
import { useNavigate } from 'react-router-dom';
import { OrderService } from "../../services/order/orderService.ts";
import { EstimatedTime } from "../../enums/EstimatedTime.ts";
import { useState } from 'react';
import { EstimatedTimeDialog } from '../../components/dialogs/EstimatedTimeDIalog.tsx';
import { playNotificationSound, checkAudioPermission, unlockAudio } from '../../utils/sound.ts';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import CourierService from "../../services/courier/courierService.ts";
import { WoltTimeline, WoltDelivery } from "../../components/wolt/WoltTimeline.tsx";
import { isAdmin } from "../../core/roles.ts";
import { LocationService } from '../../services/location/locationService.ts';
import { providerName } from '../../services/delivery/deliveryPriorityService.ts';
import * as Sentry from '@sentry/react';
import { CustomerChips, CustomerTypeChip, SourceChip, OrderSource } from '../../components/customer/CustomerChips.tsx';
import { OnSiteBadge, DineInInfo, isOnSite, onSiteColours, isPickup, statusLabel } from '../../components/order/DineIn.tsx';

type OrderItemVariant = {
    id: number;
    variant: string;
    price: string;
    name: string;
};

type OrderItemTranslation = {
    language: string;
    translatedName: string;
};

type OrderListItem = {
    id: number;
    itemTranslations: OrderItemTranslation[];
    quantity: number;
    price: number;
    imageUrl: string;
    inventoryItemVariants?: OrderItemVariant[];
};

type OrderDetails = {
    itemSubTotal: number;
    deliveryFee: number;
    serviceFee: number;
    totalPrice: number;
    orderListItems: OrderListItem[];
};

type Order = RaRecord & {
    id: number | string;
    orderNumber?: string;
    /** The restaurant's number for the day, "VLO-007" - what the kitchen and the courier call the order. */
    dailyNumber?: string | null;
    status: string;
    createdAt?: string;
    paymentMethod?: string;
    pickupTime?: string;
    customerInformation?: { name?: string; phoneNumber?: string; email?: string; defaultAddress?: string };
    orderDetails?: OrderDetails;
    assignedCourierId?: number;
    restaurantId?: number;
    isReadyForPickup?: boolean;
    notes?: string;
    woltDelivery?: WoltDelivery | null;
    /** Delivery services an admin can hand this order to now, in the city's priority order (e.g. "WOLT"). */
    handOverOptions?: string[];
    /** Where it was ordered; null when unknown (before 21 Oct 2026). */
    src?: OrderSource | null;
    /** The customer's earlier orders on Vello, any venue: 0 = NC, n = RC n. Null/absent from an older backend. */
    prev?: number | null;
    /** DELIVERY (default), DINE_IN (eats at the restaurant) or PICKUP (collects at the counter): on-site = no courier. */
    orderType?: string | null;
    dineIn?: DineInInfo | null;
};

const OrdersActions: React.FC = () => {
    const { t } = useTranslation();
    const { isConnected, error } = useOrdersList();

    const navigate = useNavigate();

    return (
        <Box
            sx={{
                paddingTop: 'var(--space-72)',
                paddingBottom: 'var(--space-32)',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                    variant="h5"
                    sx={{ paddingLeft: 'var(--space-24)', fontWeight: 700, fontSize: 'var(--text-xxl)' }}
                >
                    {t('resources.orders')}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: isConnected ? '#22c55e' : '#ef4444'
                        }}
                    />
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                        {isConnected ? 'Live' : 'Disconnected'}
                    </Typography>
                </Box>

                {error && (
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'error.main' }}>
                        {error}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 'var(--space-24)' }}>
                <Button
                    style={{
                        fontSize: 'var(--text-xs)',
                        backgroundColor: 'transparent',
                        color: 'var(--color-primary)',
                        fontWeight: 700,
                    }}
                    onClick={() => navigate(`/orders/showMap`)}
                >
                    {t('orders.showMap').toLocaleUpperCase()}
                </Button>
            </Box>
        </Box>
    );
};

export default function OrdersScreen(): JSX.Element {
    useNotify()
    return (
        <List
            actions={<OrdersActions />}
            queryOptions={{ enabled: false }}
            pagination={false}
            sx={{
                '& .RaList-main': { px: 4 },
                '& .RaList-content': {
                    border: '1px solid var(--color-secondary)',
                    borderRadius: 'var(--radius-6)',
                },
            }}
        >
            <OrdersLayout />
        </List>
    );
}

const OrdersLayout: React.FC = () => {
    const isVendor = localStorage.getItem("ra_role") === "VENDOR";
    const [location, setLocation] = React.useState<{ id: number, isManuallyClosed: boolean } | null>(null);

    React.useEffect(() => {
        if (!isVendor) return;
        new LocationService().getLocations()
            .then(locations => setLocation({
                id: locations[0].id,
                isManuallyClosed: !!locations[0].isManuallyClosed,
            }));
    }, []);

    if (isVendor && location === null) {
        return <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={36} /></Box>;
    }

    return isVendor
        ? <VendorOrdersLayout locationId={location!.id} initialManuallyClosed={location!.isManuallyClosed} />
        : <AdminOrdersLayout />;
};

const VendorOrdersLayout: React.FC<{ locationId: number, initialManuallyClosed: boolean }> = ({ locationId, initialManuallyClosed }) => {
    const { orders: wsOrders, isConnected } = useOrdersListVendor(locationId);
    return (
        <>
            <VendorStatusBar locationId={locationId} initialManuallyClosed={initialManuallyClosed} />
            <OrdersLayoutContent wsOrders={wsOrders} isConnected={isConnected} />
        </>
    );
};

const VendorStatusBar: React.FC<{ locationId: number, initialManuallyClosed: boolean }> = ({ locationId, initialManuallyClosed }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const [isManuallyClosed, setIsManuallyClosed] = React.useState<boolean>(initialManuallyClosed);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const applyStatus = async (nextManuallyClosed: boolean) => {
        setSaving(true);
        try {
            await new LocationService().updateManuallyClosedStatus(locationId, nextManuallyClosed);
            setIsManuallyClosed(nextManuallyClosed);
            notify(t('common.saved'), { type: 'success' });
        } catch (error: any) {
            notify(error?.response?.data?.error ?? t('common.error'), { type: 'error' });
            Sentry.captureException(error);
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = (_: any, checked: boolean) => {
        // checked === "accepting orders" (online); switching OFF means going offline.
        if (!checked) {
            setConfirmOpen(true);
            return;
        }
        applyStatus(false);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 'var(--space-24)',
                    py: 'var(--space-16)',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                        checked={!isManuallyClosed}
                        color="primary"
                        size="small"
                        disabled={saving}
                        onChange={handleToggle}
                    />
                    <Typography sx={{ fontWeight: 700 }}>
                        {isManuallyClosed ? t('orders.availability.offline') : t('orders.availability.online')}
                    </Typography>
                </Stack>
            </Box>

            {isManuallyClosed && (
                <Box
                    onClick={() => !saving && applyStatus(false)}
                    sx={{
                        cursor: saving ? 'default' : 'pointer',
                        bgcolor: 'var(--color-error, #d32f2f)',
                        color: '#fff',
                        px: 'var(--space-24)',
                        py: 'var(--space-16)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        fontWeight: 700,
                    }}
                >
                    <Typography sx={{ fontWeight: 700 }}>
                        {t('orders.availability.offlineBanner')}
                    </Typography>
                </Box>
            )}

            <Divider />

            <Confirm
                isOpen={confirmOpen}
                title={t('orders.availability.confirmOfflineTitle')}
                content={t('orders.availability.confirmOfflineBody')}
                confirm={t('orders.availability.confirmOfflineConfirm')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    setConfirmOpen(false);
                    await applyStatus(true);
                }}
                onClose={() => setConfirmOpen(false)}
            />
        </>
    );
};

const AdminOrdersLayout: React.FC = () => {
    const { orders: wsOrders, isConnected } = useOrdersList();
    return <OrdersLayoutContent wsOrders={wsOrders} isConnected={isConnected} />;
};

const AudioPermissionPopup: React.FC<{ onAllow: () => void }> = ({ onAllow }) => {
    const { t } = useTranslation();
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
            }}
        >
            <Card sx={{ p: 5, maxWidth: 380, textAlign: 'center', borderRadius: 'var(--radius-6)' }}>
                <Box sx={{ mb: 2 }}>
                    <VolumeUpRounded sx={{ fontSize: 56, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t('orders.enableSound')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('orders.enableSoundDescription')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('orders.enableSoundVolumeHint')}
                </Typography>
                <Button variant="contained" onClick={onAllow} size="large" fullWidth>
                    {t('orders.enableSoundButton')}
                </Button>
            </Card>
        </Box>
    );
};

const OrdersLayoutContent: React.FC<{ wsOrders: any[], isConnected: boolean }> = ({ wsOrders, isConnected }) => {
    const [selectedTab, setSelectedTab] = React.useState<'new' | 'in_progress'>('new');
    const [selectedId, setSelectedId] = React.useState<number | string | null>(null);
    const [showAudioPopup, setShowAudioPopup] = React.useState(false);
    const { t } = useTranslation();

    const prevNewOrdersCountRef = React.useRef(0);
    const isInitialLoadRef = React.useRef(true);
    const showAudioPopupRef = React.useRef(false);
    const pendingNotificationRef = React.useRef(false);

    React.useEffect(() => {
        const isVendor = localStorage.getItem("ra_role") === "VENDOR";
        if (!isVendor) return;
        checkAudioPermission().then(canPlay => {
            if (!canPlay) {
                showAudioPopupRef.current = true;
                setShowAudioPopup(true);
            }
        });
    }, []);

    React.useEffect(() => {
        const isVendor = localStorage.getItem("ra_role") === "VENDOR";
        const newOrdersCount = wsOrders.filter(order => order.orderStatus === 'PAID').length;

        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            prevNewOrdersCountRef.current = newOrdersCount;
            return;
        }

        if (newOrdersCount > prevNewOrdersCountRef.current) {
            if (isVendor) {
                if (showAudioPopupRef.current) {
                    pendingNotificationRef.current = true;
                } else {
                    playNotificationSound();
                }
            }
        }

        prevNewOrdersCountRef.current = newOrdersCount;
    }, [wsOrders]);

    React.useEffect(() => {
        const isVendor = localStorage.getItem("ra_role") === "VENDOR";
        const pendingOrders = wsOrders.filter(
            order => order.orderStatus === 'PAID'
        );

        if (pendingOrders.length === 0) {
            return;
        }

        const interval = setInterval(() => {
            if (isVendor) {
                if (showAudioPopupRef.current) {
                    pendingNotificationRef.current = true;
                } else {
                    playNotificationSound();
                }
            }
        }, 10 * 1000);

        return () => clearInterval(interval);
    }, [wsOrders]);


    const allOrders: Order[] = React.useMemo(() => {
        return wsOrders.map((order, index) => ({
            id: order.id,
            orderNumber: order.number,
            dailyNumber: order.dailyNumber ?? null,
            status: order.orderStatus,
            createdAt: order.timestamp,
            paymentMethod: order.paymentType,
            customerInformation: order.customerInformation,
            pickupTime: order.pickupTime,
            orderDetails: order.orderDetails,
            assignedCourierId: order.assignedCourierId,
            restaurantId: order.restaurantId,
            isReadyForPickup: order.isReadyForPickup,
            notes: order.notes,
            woltDelivery: order.woltDelivery,
            handOverOptions: order.handOverOptions ?? [],
            src: order.src ?? null,
            prev: order.prev ?? null,
            orderType: order.orderType ?? 'DELIVERY',
            dineIn: order.dineIn ?? null
        }));
    }, [wsOrders]);

    const filteredOrders = React.useMemo(() => {
        return allOrders.filter(order => {
            if (selectedTab === 'new') {
                return order.status === 'PAID';
            } else {
                return (order.status !== 'PAID' && order.status !== 'DELIVERED' && order.status !== 'CANCELLED');
            }
        });
    }, [allOrders, selectedTab]);

    console.log('All Orders:', allOrders);

    const newCount = allOrders.filter(o =>
        o.status === 'PAID'
    ).length;

    const inProgressCount = allOrders.filter(o =>
        (o.status !== 'PAID' && o.status !== 'DELIVERED' && o.status !== 'CANCELLED')
    ).length;

    const isLoading = !isConnected;

    React.useEffect(() => {
        if (isLoading) return;
        if (!filteredOrders.length) {
            setSelectedId(null);
            return;
        }
        const exists = selectedId != null && filteredOrders.some(r => r.id === selectedId);
        if (selectedId == null || !exists) {
            setSelectedId(filteredOrders[0].id);
        }
    }, [isLoading, filteredOrders, selectedTab]);

    const handleChangeTab = (_: any, value: 'new' | 'in_progress') => {
        setSelectedTab(value);
        setSelectedId(null);
    };

    const selectedRecord = filteredOrders.find((r) => r.id === selectedId);

    console.log("isConnected", isConnected)
    return (
        <>
        {showAudioPopup && (
            <AudioPermissionPopup
                onAllow={() => {
                    unlockAudio();
                    showAudioPopupRef.current = false;
                    setShowAudioPopup(false);
                    if (pendingNotificationRef.current) {
                        pendingNotificationRef.current = false;
                        playNotificationSound();
                    }
                }}
            />
        )}
        <Box sx={{ display: 'flex', height: 700 }}>
            <Box sx={{ width: 360, borderRight: '1px solid var(--color-secondary)' }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleChangeTab}
                    variant="fullWidth"
                    sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}
                >
                    <Tab
                        value="new"
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>{t('orders.new')}</span>
                                <Chip size="small" label={newCount} />
                            </Stack>
                        }
                    />
                    <Tab
                        value="in_progress"
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>{t('orders.inProgress')}</span>
                                <Chip size="small" label={inProgressCount} />
                            </Stack>
                        }
                    />
                </Tabs>
                <Divider />

                <OrdersListPanel
                    data={filteredOrders}
                    isLoading={isLoading}
                    selectedId={selectedId}
                    setSelectedId={(id) => setSelectedId(id)}
                />
            </Box>

            <MiddleInfoPanel record={selectedRecord ?? undefined} selectedTab={selectedTab} />

            <RightDetailsPanel record={selectedRecord ?? undefined} />
        </Box>
        </>
    );
};

const formatPickupTime = (raw?: string): string => {
    if (!raw) return '—';
    try {
        const [datePart, timePart] = raw.split(' ');
        const [day, month, year] = datePart.split('-');
        const [hour, minute] = timePart.split(':');
        const date = new Date(
            `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00Z`
        );
        return isNaN(date.getTime()) ? raw : date.toLocaleString();
    } catch (error) {
        console.error('Error parsing pickup time:', raw, error);
        return raw;
    }
};

const CountdownCircle: React.FC<{ estimatedCompletionTime: string | null }> = ({ estimatedCompletionTime }) => {
    const [minutesLeft, setMinutesLeft] = React.useState<number | null>(null);

    const parseCustomDate = (dateStr: string): Date | null => {
        try {
            const [datePart, timePart] = dateStr.split(' ');
            const [day, month, year] = datePart.split('-');
            const [hour, minute] = timePart.split(':');

            const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00Z`;

            const date = new Date(isoString);
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error('Error parsing date:', dateStr, error);
            Sentry.captureException(error);
            return null;
        }
    };

    React.useEffect(() => {
        if (!estimatedCompletionTime) {
            setMinutesLeft(null);
            return;
        }

        const completionTime = parseCustomDate(estimatedCompletionTime);

        if (!completionTime) {
            console.error('Invalid date format:', estimatedCompletionTime);
            setMinutesLeft(null);
            return;
        }

        const updateCountdown = () => {
            const now = new Date();
            const diff = completionTime.getTime() - now.getTime();
            const minutes = Math.ceil(diff / (1000 * 60));
            setMinutesLeft(Math.max(0, minutes));
        };

        updateCountdown();

        const interval = setInterval(updateCountdown, 60000);

        return () => clearInterval(interval);
    }, [estimatedCompletionTime]);

    if (minutesLeft === null) return null;

    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'var(--color-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Typography
                sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: 'black',
                }}
            >
                {minutesLeft}
            </Typography>
        </Box>
    );
};


const OrdersListPanel: React.FC<{
    data: Order[];
    isLoading?: boolean;
    selectedId: number | string | null;
    setSelectedId: (id: number | string) => void;
}> = ({ data, isLoading, selectedId, setSelectedId }) => {
    if (isLoading) {
        return (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }} aria-label="Loading orders">
                <CircularProgress size={36} />
            </Box>
        );
    }
    if (!data.length) return <Box sx={{ p: 2, color: 'text.secondary' }}>No orders</Box>;

    return (
        <Box>
            {data.map((order) => {
                const active = selectedId === order.id;
                return (
                    <Box
                        key={order.id}
                        onClick={() => setSelectedId(order.id)}
                        sx={{
                            cursor: 'pointer',
                            px: 2, py: 1.5,
                            borderLeft: active ? '3px solid var(--color-primary)' : '3px solid transparent',
                            backgroundColor: active ? 'rgba(0,0,0,0.02)' : 'transparent',
                            '&:not(:last-of-type)': { borderBottom: '1px solid var(--color-secondary)' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-xs)' }}>
                                {order.dailyNumber ? `${order.dailyNumber} · ` : ''}#{order.orderNumber ?? order.id}
                            </Typography>
                            {isOnSite(order.orderType) && (
                                <Box sx={{ mt: 0.5 }}><OnSiteBadge orderType={order.orderType} dineIn={order.dineIn} /></Box>
                            )}
                            {(order.prev != null || order.src) && (
                                <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', mt: 0.5 }}>
                                    <CustomerTypeChip prev={order.prev} unit="order" />
                                    <SourceChip src={order.src} />
                                </Box>
                            )}
                        </Box>
                        <CountdownCircle estimatedCompletionTime={order.pickupTime} />
                    </Box>
                );
            })}
        </Box>
    );
};

const MiddleInfoPanel: React.FC<{ record?: Order, selectedTab: any }> = ({ record, selectedTab }) => {
    const status = record?.status ? record.status.toUpperCase() : '—';
    const payment = record?.paymentMethod ?? '—';
    const customerName = record?.customerInformation?.name ?? '—';
    const customerPhone = record?.customerInformation?.phoneNumber ?? '—';
    const customerEmail = record?.customerInformation?.email ?? '—';
    const customerAddress = record?.customerInformation?.defaultAddress ?? '—';
    const pickupTime = formatPickupTime(record?.pickupTime);
    const assignedCourierId = record?.assignedCourierId ?? null;
    const [selectedCourier, setSelectedCourier] = useState<string>('');
    const [couriers, setCouriers] = useState<Array<{
        id: number;
        name: string;
        distanceToRestaurantInKm: number | null;
        onDelivery: boolean;
    }>>([]);
    const { t } = useTranslation();
    const notify = useNotify();

    const courierService = new CourierService();

    const getCourierList = async () => {
        try {
            return await courierService.getCourierActiveList(record?.restaurantId);
        } catch (e) {
            console.error("Error fetching courier list:", e);
            notify(e?.response.data.error || 'Internal error', { type: 'error' });
            Sentry.captureException(e);
            return [];
        }
    }

    // Only an admin picks the courier, so only an admin's panel asks who is on
    // the road. A restaurant is told a courier is coming, never which one.
    const admin = isAdmin();
    React.useEffect(() => {
        if (!record?.restaurantId || !admin) return;
        const fetchCouriers = async () => {
            const courierList = await getCourierList();
            setCouriers(courierList.data);
        };

        fetchCouriers();
        const interval = setInterval(fetchCouriers, 30 * 1000);
        return () => clearInterval(interval);
    }, [record?.restaurantId, admin]);

    React.useEffect(() => {
        console.log("Assigned Courier ID:", assignedCourierId);
        setSelectedCourier(assignedCourierId ? String(assignedCourierId) : '');
    }, [assignedCourierId, record?.id]);

    const isVendor = localStorage.getItem("ra_role") === "VENDOR";
    const [handOverTo, setHandOverTo] = useState<string | null>(null);
    const [handingOver, setHandingOver] = useState(false);

    const handOver = async (provider: string) => {
        const name = providerName(provider);
        setHandingOver(true);
        try {
            await new OrderService().handOver(record!.id, provider);
            notify(t('orders.handOver.done', { name }), { type: 'success' });
        } catch (error: any) {
            notify(error?.response?.data?.error ?? t('orders.handOver.failed', 'Could not hand the order over'), { type: 'error' });
            Sentry.captureException(error);
        } finally {
            setHandingOver(false);
            setHandOverTo(null);
        }
    };

    return (
        <Box sx={{ width: 360, borderRight: '1px solid var(--color-secondary)' }}>
            <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 1 }}>
                    {t('orders.status').toLocaleUpperCase()}
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.primary', fontWeight: 700 }}>
                    {statusLabel(status, record?.orderType)}
                </Typography>
                {isOnSite(record?.orderType) && (
                    <Box sx={{ mt: 1 }}><OnSiteBadge orderType={record?.orderType} dineIn={record?.dineIn} /></Box>
                )}
            </Box>

            <Divider />

            <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 1 }}>
                    {t('orders.paymentType').toLocaleUpperCase()}
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.primary', fontWeight: 700 }}>
                    {payment}
                </Typography>
            </Box>

            <Divider />

            {status !== "PAID" && selectedTab !== "new" && (
                <>
                    <Box sx={{ px: 2, py: 2 }}>
                        <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 1 }}>
                            {t('orders.pickupTime').toLocaleUpperCase()}
                        </Typography>
                        <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.primary', fontWeight: 700 }}>
                            {pickupTime}
                        </Typography>
                    </Box>

                    <Divider />
                </>
            )}

            {/* A dine-in or pickup order has no courier: nothing to assign or hand over. */}
            {status !== "PAID" && selectedTab !== "new" && !isVendor && !isOnSite(record?.orderType) && (
                <>
                    <Box sx={{ px: 2, py: 2 }}>
                        <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 1 }}>
                            {t('Courier').toLocaleUpperCase()}
                        </Typography>
                        {record?.woltDelivery && (
                            <WoltTimeline
                                delivery={record.woltDelivery}
                                onMarkDelivered={async () => {
                                    try {
                                        await new OrderService().markWoltOrderDelivered(record!.id);
                                    } catch (error: any) {
                                        notify(error?.response?.data?.error ?? 'Could not mark the order as delivered', { type: 'error' });
                                        Sentry.captureException(error);
                                    }
                                }}
                            />
                        )}
                        <Select
                            value={selectedCourier}
                            onChange={async (e) => {
                                try {
                                    const orderService = new OrderService();
                                    setSelectedCourier(e.target.value);
                                    await orderService.assingCourierManually(e.target.value, record!.id);
                                } catch (error: any) {
                                    notify(error.response.data.error, { type: 'error' });
                                    console.error(error.response.data.error);
                                    Sentry.captureException(error);
                                }
                            }}
                            displayEmpty
                            disabled={couriers.length === 0}
                            fullWidth
                            size="small"
                            sx={{
                                fontSize: 'var(--text-xxs)',
                                fontWeight: 700,
                                mt: 0.5
                            }}
                        >
                            {couriers?.map((courier) => (
                                <MenuItem key={courier.id} value={String(courier.id)}>
                                    <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontSize: 'var(--text-xxs)', fontWeight: 700 }}>{courier.name}</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 'var(--text-xxs)',
                                                fontWeight: 700,
                                                color: courier.onDelivery ? 'text.secondary' : 'inherit'
                                            }}
                                        >
                                            {courier.onDelivery ? `${t('orders.courierOnDelivery')} · ` : ''}
                                            {courier.distanceToRestaurantInKm != null
                                                ? `${courier.distanceToRestaurantInKm} Km`
                                                : '—'}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                        {(record?.handOverOptions?.length ?? 0) > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                                {record!.handOverOptions!.map((provider) => (
                                    <Button
                                        key={provider}
                                        size="small"
                                        variant="outlined"
                                        disabled={handingOver}
                                        onClick={() => setHandOverTo(provider)}
                                        sx={{ fontSize: 'var(--text-xxs)', fontWeight: 700 }}
                                    >
                                        {t('orders.handOver.button', { name: providerName(provider) })}
                                    </Button>
                                ))}
                            </Stack>
                        )}
                        <Confirm
                            isOpen={handOverTo !== null}
                            loading={handingOver}
                            title={t('orders.handOver.confirmTitle', { name: providerName(handOverTo ?? '') })}
                            content={t('orders.handOver.confirmBody', {
                                name: providerName(handOverTo ?? ''),
                                number: record?.orderNumber ?? ''
                            })}
                            confirm={t('orders.handOver.confirm', 'Hand over')}
                            cancel={t('common.cancel')}
                            onConfirm={() => handOverTo && handOver(handOverTo)}
                            onClose={() => setHandOverTo(null)}
                        />
                    </Box>
                    <Divider />
                </>
            )}

            <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 1 }}>
                    {t('orders.customerInformation').toLocaleUpperCase()}
                </Typography>
                <Stack spacing={0.5}>
                    <Typography sx={{ fontSize: 'var(--text-xxs)', fontWeight: 700 }}>{customerName}</Typography>
                    <CustomerChips prev={record?.prev} src={record?.src} unit="order" />
                    <Typography sx={{ fontSize: 'var(--text-xxs)' }}>{customerPhone}</Typography>
                    <Typography sx={{ fontSize: 'var(--text-xxs)' }}>{customerEmail}</Typography>
                    {!isOnSite(record?.orderType) && (
                        <Typography sx={{ fontSize: 'var(--text-xxs)' }}>{customerAddress}</Typography>
                    )}
                </Stack>
            </Box>
            <Divider />
        </Box>
    );
};

const RightDetailsPanel: React.FC<{ record?: Order }> = ({ record }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ flex: 1 }}>
            <Box>
                <Typography sx={{ fontWeight: 700, textAlign: 'center', p: 2 }}>{t('orders.orderDetails')}</Typography>
                <Divider />
            </Box>
            <Box sx={{ flex: 1, p: 2 }}>
                {!record ? (
                    <Card variant="outlined" sx={{ p: 2 }}>Select an order from the left list.</Card>
                ) : (
                    <OrderDetailsCard record={record} />
                )}
            </Box>
        </Box>
    );
};

const OrderDetailsCard: React.FC<{ record: Order }> = ({ record }) => {
    const items = record.orderDetails ?? [];
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const [estimatedTime, setEstimatedTime] = useState<EstimatedTime | null>(null);
    const notify = useNotify();
    console.log("createdAt: "+record.createdAt);

    const orderService = new OrderService();

    const cancelOrder = async (orderId: number) => {
        try {
            await orderService.cancelOrder(orderId);
        } catch (e) {
            console.error("Error cancelling order:", e);
            Sentry.captureException(e);
            notify(e?.response.data.error || 'Internal error', { type: 'error' });
        }
    }

    const confirmOrder = async (orderId: number, status: string, estimatedTime: string) => {
        if (status !== "PAID") {
            return
        }

        try {
            await orderService.confirmOrder(orderId, estimatedTime);
        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
            notify(e?.response.data.error || 'Internal error', { type: 'error' });
        }

    }

    const handleEstimatedTimeSelect = async (time: EstimatedTime) => {
        setEstimatedTime(time);

        await confirmOrder(
            record.id,
            record.status,
            time
        );
    };

    const markOrderAsReady = async (orderId: number) => {
        const orderService = new OrderService();
        try {
            await orderService.markOrderAsReady(orderId);
        } catch (e) {
            notify(e?.response.data.error || 'Internal error', { type: 'error' });
            Sentry.captureException(e);
            console.error("Error marking order as ready:", e);
        }
    }

    // Dine-in served / pickup collected: completes the order (DELIVERED). Same endpoint for both.
    const markOrderAsServed = async (orderId: number) => {
        try {
            await orderService.markOrderAsServed(orderId);
        } catch (e) {
            notify(e?.response?.data?.error || 'Internal error', { type: 'error' });
            Sentry.captureException(e);
        }
    }
    const onSite = isOnSite(record.orderType);
    const onSiteColour = onSiteColours(record.orderType);

    const getEnglishItemName = (
        translations?: { language: string; translatedName: string }[]
    ): string => {
        return (
            translations?.find(t => t.language === 'ENG')?.translatedName
            ?? translations?.[0]?.translatedName
            ?? '—'
        );
    };


    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: '16px', color: 'var(--color-text-disabled)' }}>{t('orders.orderNumber')}</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>#{record.orderNumber ?? record.id}</Typography>
            </Stack>
            {onSite && (
                <Box sx={{ mb: 1.5 }}><OnSiteBadge orderType={record.orderType} dineIn={record.dineIn} size="large" /></Box>
            )}
            {record.dailyNumber && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: '16px', color: 'var(--color-text-disabled)' }}>{t('orders.dailyNumber', "Restaurant's number today")}</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>{record.dailyNumber}</Typography>
                </Stack>
            )}
            {record.createdAt && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '16px', color: 'var(--color-text-disabled)' }}>{t('orders.createdAt')}</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                        {new Date(record.createdAt+"Z").toLocaleString()}
                    </Typography>
                </Stack>
            )}

            {record.notes && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.03)' }}>
                    <Typography sx={{ fontSize: '14px', color: 'var(--color-text-disabled)', fontWeight: 600, mb: 0.5 }}>
                        {t('orders.notes')}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                        {record.notes}
                    </Typography>
                </Box>
            )}

            <Box sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', mb: 1 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '60% 40%',
                        rowGap: 2,
                        alignItems: 'start',
                    }}
                >
                    <Typography sx={{ fontWeight: 600, color: 'var(--color-text-disabled)', fontSize: '14px' }}>
                        {t('orders.order').toLocaleUpperCase()}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: 'var(--color-text-disabled)', fontSize: '14px', textAlign: 'right' }}>
                        {t('orders.price').toLocaleUpperCase()}
                    </Typography>

                    {items.orderListItems
                        .map((it, idx) => (
                            <React.Fragment key={idx}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <Box
                                        component="img"
                                        src={it.imageUrl}
                                        alt="item's image"
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Box>
                                        <Typography sx={{ color: 'text.primary', fontWeight: 700 }}>{getEnglishItemName(it.itemTranslations)}</Typography>
                                        <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary', fontWeight: 700 }}>
                                            x{(it.quantity)}
                                        </Typography>
                                        {!!it.inventoryItemVariants?.length && (
                                            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                                                {it.inventoryItemVariants.map((variant) => (
                                                    <Typography
                                                        key={variant.id}
                                                        sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}
                                                    >
                                                        {variant.name} ({variant.variant}){variant.price ? ` · ${variant.price}` : ''}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>

                                <Typography sx={{ textAlign: 'right', fontWeight: 700 }}>€ {it.price}</Typography>
                            </React.Fragment>
                        ))}
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* A restaurant sees what the customer paid for the food. Delivery,
                service fee and the grand total are Vello's, and the server does
                not send them to a restaurant's board. */}
            {isAdmin() ? (
                <Stack spacing={2} sx={{ fontSize: 'var(--text-xxs)' }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: 'text.secondary' }}>{t('orders.itemSubtotal')}</Typography>
                        <Typography sx={{ fontWeight: 500 }}>€{items.itemSubTotal}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: 'text.secondary' }}>{t('orders.delivery')}</Typography>
                        <Typography sx={{ fontWeight: 500 }}>€{items.deliveryFee}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: 'text.secondary' }}>{t('orders.serviceFee')}</Typography>
                        <Typography sx={{ fontWeight: 500 }}>€{items.serviceFee}</Typography>
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('orders.total')}</Typography>
                        <Typography sx={{ fontWeight: 700 }}>€{items.totalPrice}</Typography>
                    </Stack>
                </Stack>
            ) : (
                <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 'var(--text-xxs)' }}>
                    <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('orders.foodTotal', 'Food total')}</Typography>
                    <Typography sx={{ fontWeight: 700 }}>€{items.itemSubTotal}</Typography>
                </Stack>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'center' }}>
                {record.status === "PAID" && (
                    <Button
                        variant="outlined"
                        onClick={() => cancelOrder(record.id)}
                        sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                    >
                        {t('common.cancel')}
                    </Button>
                )}
                {onSite && (record.status === "IN_PREPARATION" || record.status === "READY_FOR_PICKUP") && (
                    <Button
                        variant={record.isReadyForPickup ? "contained" : "outlined"}
                        onClick={() => markOrderAsServed(record.id)}
                        sx={{
                            borderRadius: 0,
                            boxShadow: 'none',
                            ...(record.isReadyForPickup
                                ? { backgroundColor: onSiteColour.solid, color: '#FFFFFF', '&:hover': { backgroundColor: onSiteColour.dark, boxShadow: 'none' } }
                                : { borderColor: onSiteColour.solid, color: onSiteColour.solid }),
                        }}
                    >
                        {isPickup(record.orderType) ? t('orders.collected', 'Collected') : t('orders.served', 'Served')}
                    </Button>
                )}
                {!record.isReadyForPickup && (
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (record.status === "IN_PREPARATION" || record.status === "ACCEPTED_BY_COURIER") {
                                await markOrderAsReady(record.id)
                            }
                            if (record.status === "PAID") {
                                setOpen(true);
                            }
                        }}
                        sx={{
                            backgroundColor: 'var(--color-primary)',
                            color: 'var(--color-text-light)',
                            borderRadius: 0,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {record.status === "PAID" ? t('orders.confirmOrder') : t('orders.ready')}
                    </Button>
                )}
            </Stack>
            <EstimatedTimeDialog
                open={open}
                onClose={() => setOpen(false)}
                onSelect={handleEstimatedTimeSelect}
            />
        </Card>
    );
};
